import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { IconCloseSvg } from '../../../assets/inventory';
import {
	ChevronDownSvg,
	ChevronSvg,
	IconGameRulesSvg,
	IconHandPowerMeterSvg,
	IconHelpSvg,
	IconInviteFriendsSvg,
	IconLanguageSvg,
	IconLogOutSvg,
	IconNotificationsSvg,
	IconPersonalDataSvg,
	IconSoundsSvg,
	IconStartTutorialSvg,
} from '../../../assets/settings';
import i18n from '../../../i18n/i18n';
import { deltaDNAService } from '../../../services/deltaDNA/deltadna.service';
import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { goldGlow } from '../../../shared/glowStyles';
import { RootState } from '../../../store';
import { logout, profileRequest, updateProfileRequest } from '../../../store/auth/authActions';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { i18nLoadRequest } from '../../../store/i18n/i18nActions';
import { env } from '../../../utils/env';
import { navigateToTab } from '../../../utils/helpers/navigationService';
import LS from '../../../utils/localStorageUtils';
import { playSound } from '../../../utils/sound';
import { RootStackParamList } from '../../navigator';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	achievementsGlow,
} from '../Achievements/achievementsTheme';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { Pressable } from '../common/Touchable';
import { copyToClipboard } from '../Friends/InviteFriends/shareInvite';
import LogoutConfirmationModal from './LogoutConfirmationModal';
import RedesignModal from './RedesignModal';
import { PopupDivider, PopupToggle } from './RedesignPopup';
import { useRedesignScale } from './useRedesignScale';

type Nav = NativeStackNavigationProp<RootStackParamList>;

const appPackage = require('../../../../package.json') as { version?: string };
const BUILD_NUMBER = appPackage.version ?? '';

/**
 * Figma "Settings / Mobile – Popup" (5054:78824) on page "PAVEL DRAFT", section "Settings": the
 * "Modal / Settings" panel (5054:78827) centred over the home screen. The panel chrome is
 * `RedesignModal`'s; everything here is the body, in the 358-wide design space scaled once.
 *
 * Colours are the file's variables off the modal (gold/50, text/primary, text/muted,
 * surface/raised, surface/sunken, surface/overlay, border/gold, icon/stroke, state/success/base).
 */
const PANEL = { height: 499, radius: 10, paddingX: 12, paddingY: 14, gap: 12 };
/**
 * The design context returns no fill for the panel, yet the artboard shows a dark maroon plate
 * with none of the home screen reading through it, so the backdrop is darkened towards
 * surface/raised to match the render.
 */
const PANEL_TINT = 'rgba(26,6,3,0.82)';
const HEADER = { height: 40, button: 32, closeIcon: 24 };
const ROW = { height: 36, gap: 5, radius: 10, paddingLeft: 14, paddingRight: 12, innerGap: 12 };
const DROPDOWN = { width: 128, height: 28, radius: 8, paddingLeft: 12, paddingRight: 8, gap: 8 };

const colors = {
	gold50: achievementsColors.gold50,
	primary: achievementsColors.primary,
	muted: achievementsColors.muted,
	/** surface/raised - every row's plate. */
	row: achievementsColors.surface,
	/** The rows' orange hairline. */
	rowBorder: achievementsColors.panelOrange,
	/** surface/sunken - the language dropdown's well. */
	sunken: achievementsColors.sunken,
	/** border/gold - the dropdown's outline and the dividers. */
	borderGold: '#E89200',
} as const;

const LANGUAGES = [
	{ code: 'en', id: 1, label: 'English' },
	{ code: 'bg', id: 2, label: 'Български' },
	{ code: 'fr', id: 3, label: 'Français' },
	{ code: 'ru', id: 4, label: 'Русский' },
	{ code: 'tr', id: 5, label: 'Türkçe' },
];

type ProfilePatch = Partial<{
	isSoundEffectsEnabled: boolean;
	isMobileNotificationsEnabled: boolean;
	isHandPowerMeterEnabled: boolean;
}>;

interface SettingsModalProps {
	onClose: () => void;
	renderInline?: boolean;
}

const SettingsModal = ({ onClose, renderInline = false }: SettingsModalProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<Nav>();
	const { navigate } = navigation;
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);

	const user = useSelector((state: RootState) => state.auth.user);
	const isLogged = useSelector(selectIsLogged);
	const { langCode } = useSelector((s: RootState) => s.i18n);

	const [sound, setSound] = useState(true);
	const [notifications, setNotifications] = useState(true);
	const [handMeter, setHandMeter] = useState(false);
	const [languageOpen, setLanguageOpen] = useState(false);
	const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);

	useEffect(() => {
		analyticsEvents.trackOpenSettings();
		dispatch(profileRequest());
	}, [dispatch]);

	useEffect(() => {
		if (!user) return;
		setSound(Boolean(user.isSoundEffectsEnabled));
		setNotifications(Boolean(user.isMobileEnabled));
		setHandMeter(Boolean(user.isHandPowerEnabled));
	}, [user]);

	const updateField = (patch: ProfilePatch) => dispatch(updateProfileRequest(patch as any));

	const selectLanguage = (code: string) => {
		setLanguageOpen(false);
		const lang = LANGUAGES.find(l => l.code === code);
		if (!lang) return;
		i18n.changeLanguage(code).catch(() => {});
		LS.set('lang', code);
		dispatch(i18nLoadRequest({ langId: lang.id, langCode: lang.code }));
	};

	// The settings popup is itself a stack screen, so a link closes it before opening its target.
	const go = (route: 'Invites' | 'GameRules' | 'PersonalData' | 'Help') => {
		onClose();
		navigate(route);
	};

	const onLogout = () => {
		analyticsEvents.trackUserLogout();
		deltaDNAService.reset();
		dispatch(logout());
		onClose();
		navigateToTab(navigation, 'Main');
	};

	const currentLanguage = LANGUAGES.find(l => l.code === langCode) ?? LANGUAGES[0];
	const versionText = `v${BUILD_NUMBER}`;
	const showBuildNumber = env('DEV_MODE', 'false') === 'true' || env('APP_ENV', '') === 'stage';
	const icon = (Icon: React.FC<any>, w = 22, h = 22) => (
		<Icon width={w * scale} height={h * scale} />
	);

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			renderInline={renderInline}
			panel={{
				// A guest has no "Log out" row, nor the divider above it.
				height: isLogged ? PANEL.height : PANEL.height - ROW.height - 1 - 2 * ROW.gap,
				radius: PANEL.radius,
				tint: PANEL_TINT,
				overScreen: true,
			}}
		>
			<View style={styles.body}>
				<View style={styles.header}>
					<View style={styles.headerButton} />
					<Text style={styles.title} numberOfLines={1}>
						{t('T_SITE_MENU_SETTINGS')}
					</Text>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_CLOSE_SETTINGS', { defaultValue: 'Close settings' })}
						onPress={onClose}
						hitSlop={8}
						style={styles.headerButton}
					>
						<IconCloseSvg width={HEADER.closeIcon * scale} height={HEADER.closeIcon * scale} />
					</Pressable>
				</View>

				<IndicatorScrollView
					style={styles.scroll}
					contentContainerStyle={styles.list}
					showsVerticalScrollIndicator={false}
				>
					<View style={[styles.row, styles.languageRow]}>
						{icon(IconLanguageSvg)}
						<Text style={styles.label} numberOfLines={1}>
							{t('T_SITE_SETTINGS_LANGUAGE')}
						</Text>
						<View>
							<Pressable
								accessibilityRole='button'
								onPress={() => setLanguageOpen(open => !open)}
								style={styles.dropdown}
							>
								<Text style={styles.dropdownLabel} numberOfLines={1}>
									{currentLanguage.label}
								</Text>
								<View style={languageOpen && styles.chevronOpen}>
									{icon(ChevronDownSvg, 16, 16)}
								</View>
							</Pressable>
							{languageOpen && (
								<View style={styles.dropdownMenu}>
									{LANGUAGES.map(lang => (
										<Pressable
											key={lang.code}
											onPress={() => selectLanguage(lang.code)}
											style={styles.dropdownItem}
										>
											<Text
												style={[
													styles.dropdownLabel,
													lang.code === currentLanguage.code && styles.dropdownLabelActive,
												]}
												numberOfLines={1}
											>
												{lang.label}
											</Text>
										</Pressable>
									))}
								</View>
							)}
						</View>
					</View>

					<SettingsRow
						styles={styles}
						icon={icon(IconSoundsSvg)}
						label={t('T_SITE_SETTINGS_SOUND')}
					>
						<PopupToggle
							scale={scale}
							value={sound}
							sound={false}
							onChange={() => {
								const next = !sound;
								playSound('ToggleSound', { force: true });
								setSound(next);
								updateField({ isSoundEffectsEnabled: next });
							}}
						/>
					</SettingsRow>
					<SettingsRow
						styles={styles}
						icon={icon(IconNotificationsSvg)}
						label={t('T_SITE_SETTINGS_NOTIFICATIONS')}
					>
						<PopupToggle
							scale={scale}
							value={notifications}
							onChange={() => {
								const next = !notifications;
								setNotifications(next);
								updateField({ isMobileNotificationsEnabled: next });
							}}
						/>
					</SettingsRow>
					<SettingsRow
						styles={styles}
						icon={icon(IconHandPowerMeterSvg)}
						label={t('T_SITE_SETTINGS_HAND_POWER_METER')}
					>
						<PopupToggle
							scale={scale}
							value={handMeter}
							onChange={() => {
								const next = !handMeter;
								setHandMeter(next);
								updateField({ isHandPowerMeterEnabled: next });
							}}
						/>
					</SettingsRow>
					{/* The invite glyph is 20x24 and sits two points closer to its label than the rest. */}
					<SettingsRow
						styles={styles}
						icon={icon(IconInviteFriendsSvg, 20, 24)}
						label={t('T_SITE_MENU_INVITES')}
						onPress={() => (isLogged ? go('Invites') : (onClose(), navigate('Login')))}
					/>

					<PopupDivider scale={scale} />

					<SettingsRow
						styles={styles}
						icon={icon(IconStartTutorialSvg)}
						label={t('T_SITE_MENU_TUTORIAL')}
						onPress={() => {
							onClose();
							if (isLogged) navigate('Tutorial', { isTutorial: true });
							else navigate('Login');
						}}
					/>
					<SettingsRow
						styles={styles}
						icon={icon(IconGameRulesSvg)}
						label={t('T_SITE_MENU_RULES')}
						onPress={() => go('GameRules')}
					/>
					<SettingsRow
						styles={styles}
						icon={icon(IconPersonalDataSvg)}
						label={t('T_SITE_MENU_PERSONAL_DATA')}
						onPress={() => go('PersonalData')}
					/>
					<SettingsRow
						styles={styles}
						icon={icon(IconHelpSvg)}
						label={t('T_SITE_MENU_CONTACTS')}
						onPress={() => go('Help')}
					/>

					{isLogged && (
						<>
							<PopupDivider scale={scale} />
							<SettingsRow
								styles={styles}
								icon={icon(IconLogOutSvg)}
								label={t('T_SITE_MENU_LOGOUT')}
								onPress={() => setShowLogoutConfirmation(true)}
							/>
						</>
					)}
				</IndicatorScrollView>
				{showBuildNumber && (
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_APP_VERSION')}
						onPress={() => {
							copyToClipboard(versionText);
						}}
					>
						<Text style={styles.buildNumber}>{versionText}</Text>
					</Pressable>
				)}
			</View>

			<LogoutConfirmationModal
				visible={showLogoutConfirmation}
				onClose={() => setShowLogoutConfirmation(false)}
				onConfirm={() => {
					setShowLogoutConfirmation(false);
					onLogout();
				}}
			/>
		</RedesignModal>
	);
};

type Styles = ReturnType<typeof createStyles>;

/** "Row / *": icon, label, and either a control or - when it navigates - the gold chevron. */
const SettingsRow = ({
	styles,
	icon,
	label,
	onPress,
	children,
}: {
	styles: Styles;
	icon: React.ReactNode;
	label: string;
	onPress?: () => void;
	children?: React.ReactNode;
}) => {
	const content = (
		<>
			{icon}
			<Text style={styles.label} numberOfLines={1}>
				{label}
			</Text>
			{children ??
				(onPress && <ChevronSvg width={styles.chevron.width} height={styles.chevron.height} />)}
		</>
	);

	if (!onPress) return <View style={styles.row}>{content}</View>;
	return (
		<Pressable
			accessibilityRole='button'
			onPress={onPress}
			style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
		>
			{content}
		</Pressable>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(PANEL.gap),
		},
		header: {
			height: s(HEADER.height),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
		headerButton: {
			width: s(HEADER.button),
			height: s(HEADER.button),
			alignItems: 'center',
			justifyContent: 'center',
		},
		title: {
			flex: 1,
			textAlign: 'center',
			fontFamily: ACHIEVEMENTS_FONT,
			fontWeight: '700',
			fontSize: s(24),
			lineHeight: s(24 * 1.3),
			color: colors.gold50,
		},
		scroll: {
			flex: 1,
			minHeight: 0,
			overflow: 'scroll',
		},
		list: {
			gap: s(ROW.gap),
		},
		buildNumber: {
			textAlign: 'center',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: s(11),
			lineHeight: s(14),
			color: colors.muted,
		},
		row: {
			height: s(ROW.height),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(ROW.innerGap),
			paddingLeft: s(ROW.paddingLeft),
			paddingRight: s(ROW.paddingRight),
			borderRadius: s(ROW.radius),
			borderWidth: Math.max(1, scale),
			borderColor: colors.rowBorder,
			backgroundColor: colors.row,
		},
		rowPressed: {
			opacity: 0.8,
		},
		// Its menu drops over the rows below, so the row has to stack above them.
		languageRow: {
			zIndex: 10,
			elevation: 10,
		},
		label: {
			flex: 1,
			fontFamily: ACHIEVEMENTS_FONT,
			fontWeight: '600',
			fontSize: s(16),
			lineHeight: s(20),
			color: colors.gold50,
		},
		chevron: {
			width: s(20),
			height: s(20),
		},
		chevronOpen: {
			transform: [{ rotate: '180deg' }],
		},
		dropdown: {
			width: s(DROPDOWN.width),
			height: s(DROPDOWN.height),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(DROPDOWN.gap),
			paddingLeft: s(DROPDOWN.paddingLeft),
			paddingRight: s(DROPDOWN.paddingRight),
			borderRadius: s(DROPDOWN.radius),
			borderWidth: Math.max(1, scale),
			borderColor: colors.borderGold,
			backgroundColor: colors.sunken,
			...goldGlow,
		},
		dropdownLabel: {
			flex: 1,
			fontFamily: ACHIEVEMENTS_FONT,
			fontWeight: '500',
			fontSize: s(14),
			lineHeight: s(18),
			color: colors.primary,
		},
		dropdownLabelActive: {
			color: colors.gold50,
			fontWeight: '700',
		},
		dropdownMenu: {
			position: 'absolute',
			top: s(DROPDOWN.height + 4),
			left: 0,
			width: s(DROPDOWN.width),
			paddingVertical: s(4),
			borderRadius: s(DROPDOWN.radius),
			borderWidth: Math.max(1, scale),
			borderColor: colors.borderGold,
			backgroundColor: colors.sunken,
			...achievementsGlow.menu,
		},
		dropdownItem: {
			height: s(DROPDOWN.height),
			justifyContent: 'center',
			paddingLeft: s(DROPDOWN.paddingLeft),
			paddingRight: s(DROPDOWN.paddingRight),
		},
	});
};

export default SettingsModal;
